import { useEffect } from 'react'
import { useQueryClient } from '@tanstack/react-query'

/**
 * Custom hook for implementing query persistence using localStorage
 * This is a temporary solution until TanStack Query version conflicts are resolved
 */
export function useQueryPersistence() {
  const queryClient = useQueryClient()

  useEffect(() => {
    // Restore query cache from localStorage on mount
    const restoreCache = () => {
      try {
        const storedCache = localStorage.getItem('resi4rent-query-cache')
        if (storedCache) {
          const cache = JSON.parse(storedCache)

          // Only restore if cache is not too old (24 hours)
          const cacheAge = Date.now() - (cache.timestamp || 0)
          const maxAge = 24 * 60 * 60 * 1000 // 24 hours

          if (cacheAge < maxAge && cache.data) {
            // Restore each query individually
            Object.entries(cache.data).forEach(([queryKey, queryData]: [string, any]) => {
              try {
                queryClient.setQueryData(JSON.parse(queryKey), queryData.data, {
                  updatedAt: queryData.updatedAt
                })
              } catch (error) {
                console.warn('Failed to restore query:', queryKey, error)
              }
            })
            console.log('Query cache restored from localStorage')
          }
        }
      } catch (error) {
        console.error('Failed to restore query cache:', error)
        // Clear corrupted cache
        localStorage.removeItem('resi4rent-query-cache')
      }
    }

    // Save query cache to localStorage periodically
    const saveCache = () => {
      try {
        const cache = queryClient.getQueryCache()
        const queries = cache.getAll()

        const dataToStore: Record<string, any> = {}

        queries.forEach((query) => {
          if (query.state.data && query.queryKey) {
            const keyString = JSON.stringify(query.queryKey)
            dataToStore[keyString] = {
              data: query.state.data,
              updatedAt: query.state.dataUpdatedAt
            }
          }
        })

        const cacheData = {
          timestamp: Date.now(),
          data: dataToStore
        }

        localStorage.setItem('resi4rent-query-cache', JSON.stringify(cacheData))
      } catch (error) {
        console.error('Failed to save query cache:', error)
      }
    }

    // Restore cache on mount
    restoreCache()

    // Save cache every 30 seconds
    const interval = setInterval(saveCache, 30000)

    // Save cache before unload
    const handleBeforeUnload = () => {
      saveCache()
    }

    window.addEventListener('beforeunload', handleBeforeUnload)

    return () => {
      clearInterval(interval)
      window.removeEventListener('beforeunload', handleBeforeUnload)
      saveCache() // Save one last time
    }
  }, [queryClient])
}
